import {
  DashboardStats,
  ChannelMetricsData,
  Settings,
  Task,
  LogEntry,
  Channel,
  TruncateMetricsResult,
} from "./types";

const API_BASE = "/api/v1";

class APIClient {
  private async request<T>(
    endpoint: string,
    options?: RequestInit & { timeout?: number },
  ): Promise<T> {
    // Add session token to URL if available
    const sessionToken = sessionStorage.getItem("session_token");
    const separator = endpoint.includes("?") ? "&" : "?";
    const url = sessionToken
      ? `${API_BASE}${endpoint}${separator}session_token=${sessionToken}`
      : `${API_BASE}${endpoint}`;

    // Setup timeout using AbortController
    const controller = new AbortController();
    const timeoutId = options?.timeout
      ? setTimeout(() => controller.abort(), options.timeout)
      : null;

    try {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...options?.headers,
        },
        ...options,
        signal: controller.signal,
      });

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} ${response.statusText}`;
        console.error(
          "[API] Request failed:",
          endpoint,
          response.status,
          response.statusText,
        );
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorMessage += ` - ${errorBody}`;
            console.error("[API] Error body:", errorBody.substring(0, 500));
          }
        } catch (e) {
          console.error("[API] Could not read error response:", e);
        }
        throw new Error(errorMessage);
      }

      // Don't try to parse JSON for 204 No Content responses
      if (response.status === 204) {
        return {} as T;
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      if (error.name === "AbortError") {
        throw new Error(
          "Request timeout - the webhook endpoint did not respond within 30 seconds",
        );
      }
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  // Generic HTTP methods
  async get<T = any>(
    endpoint: string,
    options?: { timeout?: number },
  ): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, options);
    return { data };
  }

  async post<T = any>(
    endpoint: string,
    body?: any,
    options?: { timeout?: number },
  ): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(body),
      ...options,
    });
    return { data };
  }

  async put<T = any>(
    endpoint: string,
    body?: any,
    options?: { timeout?: number },
  ): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(body),
      ...options,
    });
    return { data };
  }

  async delete<T = any>(
    endpoint: string,
    options?: { timeout?: number },
  ): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: "DELETE",
      ...options,
    });
    return { data };
  }

  // Dashboard Stats
  async getDashboardStats(
    bucketSeconds?: number,
    channelId?: string,
    gatewayId?: string,
    identityDid?: string,
    pipeId?: string,
  ): Promise<DashboardStats> {
    const params = new URLSearchParams();
    if (bucketSeconds)
      params.append("bucket_seconds", bucketSeconds.toString());
    if (channelId) params.append("channel_id", channelId);
    if (gatewayId) params.append("gateway_id", gatewayId);
    if (identityDid) params.append("identity_did", identityDid);
    if (pipeId) params.append("pipe_id", pipeId);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    return this.request<DashboardStats>(`/dashboard/stats${queryString}`);
  }

  async getChannelMetrics(
    channelConfigId: string,
  ): Promise<ChannelMetricsData> {
    return this.request<ChannelMetricsData>(
      `/dashboard/channel/${encodeURIComponent(channelConfigId)}/metrics`,
    );
  }

  async getHierarchicalMetrics(): Promise<any> {
    return this.request("/dashboard/metrics/hierarchical");
  }

  // Identity Management
  async resolveIdentityDID(did: string): Promise<any> {
    return this.request(`/identity/resolve-did?did=${encodeURIComponent(did)}`);
  }

  // Settings Management
  async getSettings(): Promise<Settings> {
    const backendSettings = await this.request<any>("/settings");

    // Convert backend settings format to frontend format
    return {
      badge_threshold: backendSettings.badge_threshold_minutes || 5,
      metrics_retention: backendSettings.metrics_retention_minutes || 360, // Both in minutes now
      task_activity_window: backendSettings.task_activity_window_seconds || 60,
      connections_window: backendSettings.connections_window_minutes || 5,
      latency_window: backendSettings.latency_window_minutes || 5,
      onboarding_channel_ttl_seconds:
        backendSettings.onboarding_channel_ttl_seconds || 30,
      refresh_interval_seconds: backendSettings.refresh_interval_seconds || 5,
      log_timestamp_format: backendSettings.log_timestamp_format || "local",
      bucket_seconds: backendSettings.bucket_seconds || 30,
    };
  }

  async updateSettings(settings: Partial<Settings>): Promise<void> {
    // Map frontend field names to backend field names
    const backendSettings: any = {};

    if (settings.badge_threshold !== undefined) {
      backendSettings.badge_threshold_minutes = settings.badge_threshold;
    }

    if (settings.metrics_retention !== undefined) {
      backendSettings.metrics_retention_minutes = settings.metrics_retention;
    }

    if (settings.task_activity_window !== undefined) {
      backendSettings.task_activity_window_seconds =
        settings.task_activity_window;
    }

    if (settings.connections_window !== undefined) {
      backendSettings.connections_window_minutes = settings.connections_window;
    }

    if (settings.latency_window !== undefined) {
      backendSettings.latency_window_minutes = settings.latency_window;
    }

    if (settings.onboarding_channel_ttl_seconds !== undefined) {
      backendSettings.onboarding_channel_ttl_seconds =
        settings.onboarding_channel_ttl_seconds;
    }

    if (settings.refresh_interval_seconds !== undefined) {
      backendSettings.refresh_interval_seconds =
        settings.refresh_interval_seconds;
    }

    if (settings.log_timestamp_format !== undefined) {
      backendSettings.log_timestamp_format = settings.log_timestamp_format;
    }

    if (settings.bucket_seconds !== undefined) {
      backendSettings.bucket_seconds = settings.bucket_seconds;
    }

    await this.request("/settings", {
      method: "POST",
      body: JSON.stringify(backendSettings),
    });
  }

  async resetSettings(): Promise<void> {
    await this.request("/settings", {
      method: "DELETE",
    });
  }

  // Channel Management
  async createChannel(channelConfig: any): Promise<{ config_id: string }> {
    // Generate a unique config_id for the new channel
    const configId = crypto.randomUUID();
    const channelWithId = { ...channelConfig, config_id: configId };

    await this.request(`/channels/${encodeURIComponent(configId)}`, {
      method: "PUT",
      body: JSON.stringify(channelWithId),
    });

    return { config_id: configId };
  }

  async updateChannel(
    configId: string,
    channelConfig: Partial<Channel>,
  ): Promise<void> {
    await this.request(`/channels/${encodeURIComponent(configId)}`, {
      method: "PUT",
      body: JSON.stringify(channelConfig),
    });
  }

  async deleteChannel(configId: string): Promise<void> {
    await this.request(`/channels/${encodeURIComponent(configId)}`, {
      method: "DELETE",
    });
  }

  async reloadChannel(configId: string): Promise<void> {
    await this.request(`/config/reload/${encodeURIComponent(configId)}`, {
      method: "POST",
    });
  }

  async reloadAllChannels(): Promise<void> {
    await this.request("/config/reload", {
      method: "POST",
    });
  }

  // Onboarding Management
  async createTempOnboardChannel(protocol: string): Promise<{
    config_id: string;
    channel_name: string;
    endpoint_url: string;
    message: string;
    ttl_seconds: number;
  }> {
    return this.request("/onboard/create-temp-channel", {
      method: "POST",
      body: JSON.stringify({ protocol }),
    });
  }

  async deleteTempOnboardChannel(configId: string): Promise<void> {
    await this.request(
      `/onboard/delete-temp-channel/${encodeURIComponent(configId)}`,
      {
        method: "DELETE",
      },
    );
  }

  // Metrics Management
  async truncateMetrics(): Promise<TruncateMetricsResult> {
    return this.request<TruncateMetricsResult>("/metrics/truncate", {
      method: "POST",
    });
  }

  // Tasks (if available - placeholder for future implementation)
  async getTasks(): Promise<Task[]> {
    // This might need to be implemented in the backend
    return this.request<Task[]>("/tasks");
  }

  // Logs (if available - placeholder for future implementation)
  async getLogs(limit?: number): Promise<LogEntry[]> {
    const params = limit ? `?limit=${limit}` : "";
    return this.request<LogEntry[]>(`/logs${params}`);
  }

  // Permissions
  async getPermissions(): Promise<Record<string, boolean>> {
    return this.request<Record<string, boolean>>("/permissions");
  }
}

export const apiClient = new APIClient();
